// Admin-only "Body Signal — Prototype" card.
// Gated to profiles.is_test_user (same mechanism as /admin).
// READ-ONLY. Reads daily_logs + wearable_metrics for the signed-in admin's
// own user_id and shows an observational split on Headaches severity.
// No writes, no new table, does not touch any existing component.
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import {
  computeBodySignal,
  type BodySignalOutput,
  type LogRow,
  type SignalResult,
  type WearRow,
} from "@/knowmeno/bodySignalPrototype";

export const Route = createFileRoute("/admin/body-signal")({
  head: () => ({
    meta: [
      { title: "Body Signal Prototype · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BodySignalPage,
});

type State =
  | { status: "loading" }
  | { status: "unauthenticated" }
  | { status: "forbidden" }
  | { status: "ok"; result: BodySignalOutput };

function BodySignalPage() {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      const session = sess.session;
      if (!session) {
        if (!cancelled) setState({ status: "unauthenticated" });
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_test_user")
        .eq("id", session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (!profile?.is_test_user) {
        setState({ status: "forbidden" });
        return;
      }

      const uid = session.user.id;
      const [logsRes, wearRes] = await Promise.all([
        supabase
          .from("daily_logs")
          .select("log_date, symptom_name, severity")
          .eq("user_id", uid),
        supabase
          .from("wearable_metrics")
          .select("metric_date, hrv_ms, sleep_duration_minutes, steps")
          .eq("user_id", uid),
      ]);
      if (cancelled) return;

      const logs = (logsRes.data ?? []) as LogRow[];
      const wear = (wearRes.data ?? []) as WearRow[];
      const result = computeBodySignal(logs, wear);
      setState({ status: "ok", result });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (state.status === "unauthenticated") {
    return (
      <Centered>
        You need to be signed in.{" "}
        <Link to="/sign-in" className="underline">Sign in</Link>.
      </Centered>
    );
  }

  if (state.status === "forbidden") {
    return <Centered>Not available.</Centered>;
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <ResultCard result={state.result} />
      <p className="mt-4 text-xs text-neutral-500">
        Prototype for internal review. Not a medical finding. Not shown to users.
      </p>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-md mx-auto p-8 text-center text-sm text-neutral-600">
      {children}
    </div>
  );
}

function ResultCard({ result }: { result: BodySignalOutput }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-neutral-900">
        Body Signal (Prototype — your data only)
      </h2>

      {result.status === "insufficient" ? (
        <div className="mt-3 text-sm text-neutral-700">
          <p>
            {result.headacheDays} paired day{result.headacheDays === 1 ? "" : "s"} with Headaches logs so far
            {" "}(across {result.pairedDays} paired days total).
          </p>
          <p className="mt-2 text-neutral-600">
            Keep logging — insights unlock at 14 paired days.
          </p>
        </div>
      ) : (
        <>
          <p className="mt-2 text-sm text-neutral-600">
            Based on {result.headacheDays} paired days of your own logs. This is an early prototype,
            not a medical finding.
          </p>
          <ul className="mt-4 space-y-3 text-sm text-neutral-800">
            <li>{sentence("HRV", "ms", result.hrv_ms, (v) => v.toFixed(1))}</li>
            <li>{sentence("sleep duration", "min", result.sleep_duration_minutes, (v) => Math.round(v).toString())}</li>
            <li>{sentence("steps", "", result.steps, (v) => Math.round(v).toLocaleString())}</li>
          </ul>
        </>
      )}
    </div>
  );
}

function sentence(
  label: string,
  unit: string,
  r: SignalResult,
  fmt: (n: number) => string,
): string {
  if (r.direction === "insufficient" || r.higherAvg == null || r.lowerAvg == null) {
    return `Not enough ${label} data on paired days yet.`;
  }
  if (r.direction === "no-clear-difference") {
    return `No clear difference for ${label}.`;
  }
  const u = unit ? ` ${unit}` : "";
  return `On days you logged higher headaches, your ${label} averaged ${fmt(r.higherAvg)}${u} vs ${fmt(r.lowerAvg)}${u} on lower-headache days (${r.higherN} vs ${r.lowerN} days).`;
}
